import { CreditCard, Calendar, DollarSign, Loader2, AlertCircle, CheckCircle2, Clock, Crown, Zap, Star, Users, ArrowRight, Gift, ShoppingCart, ArrowLeft } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '../../constants/routes';
import { Button } from '../../components/ui/Button';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useSaaSSubscription } from '../../hooks/useSaaSSubscription';
import { supabase } from '../../lib/supabase';

interface PricingOption {
  id: string;
  name: string;
  investorName: string; // Investor/partner tier name
  duration: string;
  monthlyPrice: number;
  totalPrice: number;
  discount: number;
  discountLabel: string;
  isPopular?: boolean;
  isGoldenTicket?: boolean;
  features: string[];
  perks?: string[];
  benefits?: string[];
  colorScheme: {
    border: string;
    bgGradient: string;
    textAccent: string;
    badgeBg: string;
    badgeBorder: string;
  };
}

export default function RollOpsSubscriptionPage() {
  const navigate = useNavigate();
  const { data: companyId, role } = useCurrentCompany();
  const { data: subscription, isLoading, error } = useSaaSSubscription();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // Get location count
  const { data: locationCount = 1 } = useQuery({
    queryKey: ['company-locations-count', companyId],
    enabled: !!companyId,
    queryFn: async () => {
      if (!companyId) return 1;
      const { count, error } = await supabase
        .from('locations')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('is_active', true);
      
      if (error) {
        console.error('[RollOpsSubscription] Error fetching locations:', error);
        return 1; // Default to 1 location
      }
      return Math.max(1, count || 1); // At least 1 location
    },
  });

  // Founders Pricing: $20/month per location
  const foundersMonthlyPrice = 20;
  const monthlyPricePerLocation = foundersMonthlyPrice * locationCount;

  // Pricing options - MUST be before any early returns (React Hook rules)
  const pricingOptions: PricingOption[] = useMemo(() => [
    {
      id: 'monthly',
      name: 'Monthly',
      investorName: 'Flex Partner',
      duration: 'Month-to-Month',
      monthlyPrice: monthlyPricePerLocation,
      totalPrice: monthlyPricePerLocation,
      discount: 0,
      discountLabel: 'Founders Price',
      colorScheme: {
        border: 'border-blue-500',
        bgGradient: 'from-blue-500/10 to-blue-600/5',
        textAccent: 'text-blue-400',
        badgeBg: 'bg-blue-500/20',
        badgeBorder: 'border-blue-500',
      },
      features: [
        'All Pro features',
        'Month-to-month flexibility',
        'Cancel anytime',
        'Zero credit card processing fees',
        'Complete digital signup flow',
        'Member management',
        'Automated billing',
        'Class scheduling & attendance',
      ],
      benefits: [
        'Standard onboarding support',
        'Email support',
        'Access to all core features',
      ],
    },
    {
      id: '1year',
      name: '1 Year',
      investorName: 'Associate Partner',
      duration: '12 Months',
      monthlyPrice: monthlyPricePerLocation * 0.95, // 5% discount
      totalPrice: monthlyPricePerLocation * 0.95 * 12,
      discount: 5,
      discountLabel: '5% Off',
      colorScheme: {
        border: 'border-gray-400',
        bgGradient: 'from-gray-500/10 to-gray-600/5',
        textAccent: 'text-gray-300',
        badgeBg: 'bg-gray-500/20',
        badgeBorder: 'border-gray-400',
      },
      features: [
        'All Pro features',
        '5% discount',
        'Locked-in rate for 1 year',
        'Zero credit card processing fees',
        'Complete digital signup flow',
        'Member management',
        'Automated billing',
        'Class scheduling & attendance',
      ],
      benefits: [
        'Standard onboarding support',
        'Priority email support',
        'Early access to new features',
        'Access to all core features',
      ],
    },
    {
      id: '2year',
      name: '2 Years',
      investorName: 'Champion Partner',
      duration: '24 Months',
      monthlyPrice: monthlyPricePerLocation * 0.90, // 10% discount
      totalPrice: monthlyPricePerLocation * 0.90 * 24,
      discount: 10,
      discountLabel: '10% Off',
      colorScheme: {
        border: 'border-purple-500',
        bgGradient: 'from-purple-500/10 via-purple-600/8 to-purple-700/5',
        textAccent: 'text-purple-300',
        badgeBg: 'bg-purple-500/20',
        badgeBorder: 'border-purple-400',
      },
      features: [
        'All Pro features',
        '10% discount',
        'Locked-in rate for 2 years',
        'Zero credit card processing fees',
        'Complete digital signup flow',
        'Member management',
        'Automated billing',
        'Class scheduling & attendance',
      ],
    },
    {
      id: '3year',
      name: '3 Years',
      investorName: 'Equity Partner',
      duration: '36 Months',
      monthlyPrice: monthlyPricePerLocation * 0.80, // 20% discount
      totalPrice: monthlyPricePerLocation * 0.80 * 36,
      discount: 20,
      discountLabel: '20% Off',
      isPopular: true,
      isGoldenTicket: true,
      colorScheme: {
        border: 'border-yellow-500',
        bgGradient: 'from-yellow-500/15 via-amber-500/12 to-yellow-600/8',
        textAccent: 'text-yellow-300',
        badgeBg: 'bg-gradient-to-r from-yellow-500/30 to-amber-500/25',
        badgeBorder: 'border-yellow-400',
      },
      features: [
        'All Pro features',
        '20% discount (Best Value)',
        'Locked-in rate for 3 years',
        'Zero credit card processing fees',
        'Complete digital signup flow',
        'Member management',
        'Automated billing',
        'Class scheduling & attendance',
      ],
      benefits: [
        'CEO on-site onboarding at your gym (First 10 seats only)',
        'Live tech staff support during conversion (First 10 seats only)',
        'Priority Discord chat with CEO (First 10 seats only)',
        'Priority Discord support channel (All Equity Partners)',
        'Tailored onboarding support (All Equity Partners)',
        'Concierge onboarding support',
        'Priority issue resolution',
        'First rights to new features',
        'Private Discord support channel',
        'Champions Club membership',
        'Exclusive training sessions',
        'Direct product feedback channel',
        'Access to all core features',
        'Revenue share participation opportunity',
      ],
      perks: [
        'CEO on-site onboarding at your gym (First 10 seats only)',
        'Live tech staff support during conversion (First 10 seats only)',
        'Priority Discord chat with CEO (First 10 seats only)',
        'Priority Discord support channel (All Equity Partners)',
        'Tailored onboarding support (All Equity Partners)',
        'Concierge onboarding support',
        'Real human support: Remote video, phone, chat, or on-site when needed',
        'Priority issue resolution',
        'First rights to new features',
        'Champions Club membership',
        'Exclusive training sessions',
        'Direct product feedback channel',
        'Revenue share participation opportunity (private equity stake)',
      ],
    },
  ], [monthlyPricePerLocation]);

  // Only owners and admins can access this page
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';

  if (!isOwnerOrAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h2>
          <p className="text-text-secondary">Only owners and admins can access subscription management.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Error Loading Subscription</h2>
          <p className="text-text-secondary">{error ? String(error) : 'Failed to load subscription'}</p>
        </div>
      </div>
    );
  }

  const isOnTrial = subscription?.status === 'trialing' && subscription?.trial_end_date;
  const trialDaysRemaining = isOnTrial && subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handlePlanClick = (optionId: string) => {
    setSelectedPlanId(optionId);
  };

  const handleProceedToCheckout = async () => {
    if (!selectedPlanId || !companyId) {
      alert('Please select a plan first');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        'create-saas-subscription-checkout',
        {
          body: {
            planId: selectedPlanId,
            locationCount,
          },
        }
      );

      if (error) {
        console.error('[RollOpsSubscription] Error creating checkout:', error);
        alert(`Failed to create checkout session: ${error.message || 'Unknown error'}`);
        return;
      }

      if (data?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (err) {
      console.error('[RollOpsSubscription] Error:', err);
      alert(`Failed to create checkout session: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Core features that are the same for all plans
  const coreFeatures = [
    'Zero credit card processing fees',
    'Complete digital signup flow',
    'Member management',
    'Automated billing',
    'Class scheduling & attendance',
    'All Pro features',
  ];

  // Calculate total savings over contract period compared to monthly
  const getTotalSavings = (option: PricingOption): number => {
    if (option.id === 'monthly') return 0;
    const monthlySavingsPerLocation = foundersMonthlyPrice - (foundersMonthlyPrice * (1 - option.discount / 100));
    const months = option.id === '1year' ? 12 : option.id === '2year' ? 24 : 36;
    return monthlySavingsPerLocation * months * locationCount;
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] w-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  // Get color classes for each plan
  const getPlanColors = (optionId: string, isSelected: boolean) => {
    const base = {
      monthly: {
        border: isSelected ? 'border-blue-500' : 'border-blue-500/50',
        bg: 'bg-gradient-to-br from-blue-500/10 to-blue-600/5',
        text: 'text-blue-400',
        badge: 'bg-blue-500/20',
        badgeBorder: 'border-blue-500/50',
        shadow: 'shadow-blue-500/30',
      },
      '1year': {
        border: isSelected ? 'border-gray-400' : 'border-gray-400/50',
        bg: 'bg-gradient-to-br from-gray-500/10 to-gray-600/5',
        text: 'text-gray-300',
        badge: 'bg-gray-500/20',
        badgeBorder: 'border-gray-400/50',
        shadow: 'shadow-gray-400/30',
      },
      '2year': {
        border: isSelected ? 'border-purple-500' : 'border-purple-500/50',
        bg: 'bg-gradient-to-br from-purple-500/10 via-purple-600/8 to-purple-700/5',
        text: 'text-purple-300',
        badge: 'bg-purple-500/20',
        badgeBorder: 'border-purple-400/50',
        shadow: 'shadow-purple-500/30',
      },
      '3year': {
        border: isSelected ? 'border-yellow-500' : 'border-yellow-500/50',
        bg: 'bg-gradient-to-br from-yellow-500/15 via-amber-500/12 to-yellow-600/8',
        text: 'text-yellow-300',
        badge: 'bg-gradient-to-r from-yellow-500/30 to-amber-500/25',
        badgeBorder: 'border-yellow-400/60',
        shadow: 'shadow-yellow-500/30',
      },
    };
    return base[optionId as keyof typeof base] || base.monthly;
  };

  // Get key benefits/features to display on compact cards
  const getCardBenefits = (option: PricingOption): string[] => {
    const benefits: string[] = [];
    
    if (option.id === 'monthly') {
      benefits.push('Month-to-month flexibility', 'Cancel anytime', 'No commitment');
    } else if (option.id === '1year') {
      benefits.push(`Save $${getTotalSavings(option).toFixed(0)}`, 'Priority email support', 'Early access to features', 'Locked-in rate');
    } else if (option.id === '2year') {
      benefits.push(`Save $${getTotalSavings(option).toFixed(0)}`, '10% discount', 'Locked-in rate', 'Best value');
    } else if (option.id === '3year') {
      benefits.push(`Save $${getTotalSavings(option).toFixed(0)}`, '20% discount (Best Value)', 'Founders Circle (First 10)', 'CEO on-site onboarding', 'Priority Discord chat with CEO');
    }
    
    return benefits;
  };

  // Get bonus features for selected plan (what makes it special)
  const getBonusFeatures = (option: PricingOption) => {
    const bonuses: string[] = [];
    
    if (option.id === 'monthly') {
      bonuses.push('Month-to-month flexibility', 'Cancel anytime');
    } else if (option.id === '1year') {
      bonuses.push(`${option.discount}% discount`, 'Locked-in rate for 1 year', 'Priority email support', 'Early access to new features');
    } else if (option.id === '2year') {
      bonuses.push(`${option.discount}% discount`, 'Locked-in rate for 2 years');
    } else if (option.id === '3year') {
      bonuses.push(`${option.discount}% discount (Best Value)`, 'Locked-in rate for 3 years', 'Founders Circle: CEO on-site onboarding (First 10)', 'Live tech staff during conversion (First 10)', 'Priority Discord chat with CEO (First 10)', 'Priority Discord support channel (All Equity Partners)', 'Champions Club membership');
    }
    
    return bonuses;
  };

  const selectedPlan = pricingOptions.find(opt => opt.id === selectedPlanId);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page Header - matches Billing & Subscriptions styling */}
      <div className="border-b border-white/10 bg-[#111010] pb-2 md:pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">
              Choose Your Plan
            </h1>
            <p className="text-white/80 mt-1">
              All plans include the same powerful RollOps features
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.BILLING)}
            className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Billing
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-auto bg-[#111010]">
        <div className="flex-1 w-full min-h-full py-4">
        {/* Trial Status Banner */}
        {isOnTrial && (
          <div className="bg-gradient-to-r from-yellow-600/20 to-yellow-500/10 border-2 border-yellow-600/50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-text-primary font-semibold">
                  {trialDaysRemaining > 0 
                    ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining on your free trial`
                    : 'Your trial is ending soon'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Founders Pricing - Compact */}
        <div className="bg-gradient-to-r from-green-600/20 to-green-700/20 border border-green-600 rounded-xl p-4 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-text-primary bg-green-600/20 px-2 py-1 rounded">FOUNDERS PRICING</span>
                <span className="text-sm text-text-tertiary">${foundersMonthlyPrice}/month per location</span>
              </div>
              <p className="text-sm text-text-secondary">
                {locationCount} location{locationCount !== 1 ? 's' : ''} = ${monthlyPricePerLocation.toFixed(2)}/month
              </p>
            </div>
            <p className="text-xs text-text-tertiary">Limited time only</p>
          </div>
        </div>

        {/* Compact Plan Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {pricingOptions.map((option) => {
            const isSelected = selectedPlanId === option.id;
            const colors = getPlanColors(option.id, isSelected);
            return (
              <button
                key={option.id}
                onClick={() => handlePlanClick(option.id)}
                className={`
                  relative rounded-lg border-2 transition-all duration-200 text-left p-4 overflow-hidden
                  ${colors.bg}
                  ${colors.border}
                  ${isSelected ? `shadow-lg ${colors.shadow}` : 'hover:opacity-80'}
                `}
              >
                {/* Investor Name Badge */}
                <div className={`absolute top-0 right-0 ${colors.badge} ${colors.badgeBorder} border-l-2 border-b-2 rounded-bl-lg px-2 py-1`}>
                  <p className={`text-xs font-bold ${colors.text}`}>
                    {option.investorName}
                  </p>
                </div>
                
                <div className="mb-2 mt-6">
                  <h3 className="text-lg font-bold text-text-primary">{option.name}</h3>
                  <p className="text-xs text-text-tertiary">{option.duration}</p>
                </div>
                
                <div className="mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-text-primary">
                      ${(option.discount > 0 
                        ? foundersMonthlyPrice * (1 - option.discount / 100)
                        : foundersMonthlyPrice
                      ).toFixed(2)}
                    </span>
                    <span className="text-sm text-text-tertiary">/mo</span>
                  </div>
                  <p className="text-xs text-text-tertiary mt-1">
                    ${option.monthlyPrice.toFixed(2)} for {locationCount} location{locationCount !== 1 ? 's' : ''}
                  </p>
                  {option.discount > 0 && (
                    <p className="text-xs text-green-400 mt-0.5">{option.discountLabel}</p>
                  )}
                </div>

                {/* Savings and Benefits */}
                <div className="mt-3 pt-3 border-t border-[#504A4A]">
                  {getTotalSavings(option) > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-green-400">
                        Save ${getTotalSavings(option).toFixed(0)}
                      </p>
                    </div>
                  )}
                  <div className="space-y-1">
                    {getCardBenefits(option).slice(0, 3).map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-text-secondary leading-tight">{benefit}</p>
                      </div>
                    ))}
                    {getCardBenefits(option).length > 3 && (
                      <p className="text-xs text-text-tertiary italic">
                        +{getCardBenefits(option).length - 3} more benefits
                      </p>
                    )}
                  </div>
                </div>
                
                {isSelected && (
                  <div className="mt-2 pt-2 border-t border-[#504A4A]">
                    <p className="text-xs text-green-400 font-semibold">✓ Selected</p>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Expanded Plan Details */}
        {selectedPlan && (
          <div className={`rounded-xl border-2 p-8 mb-8 ${getPlanColors(selectedPlan.id, true).bg} ${getPlanColors(selectedPlan.id, true).border}`}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${getPlanColors(selectedPlan.id, true).badge} ${getPlanColors(selectedPlan.id, true).badgeBorder} border ${getPlanColors(selectedPlan.id, true).text}`}>
                        {selectedPlan.investorName}
                      </span>
                      {selectedPlan.isGoldenTicket && (
                        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-[#221F1F] px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          GOLDEN TICKET
                        </div>
                      )}
                    </div>
                    <h2 className="text-3xl font-bold text-text-primary">{selectedPlan.name}</h2>
                  </div>
                </div>
                {/* Pricing Display */}
                {selectedPlan.discount > 0 ? (
                  <div className="space-y-2">
                    {/* Base Price */}
                    <div className="text-sm text-text-tertiary">
                      ${foundersMonthlyPrice.toFixed(2)}/month per location
                    </div>
                    {/* Discount as Main Large Number */}
                    <div className="flex items-baseline gap-3">
                      <span className="text-6xl font-bold text-green-400">
                        {selectedPlan.discount}%
                      </span>
                      <span className="text-2xl font-bold text-green-400">OFF</span>
                    </div>
                    {/* Discounted Price */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-text-primary">
                        ${(foundersMonthlyPrice * (1 - selectedPlan.discount / 100)).toFixed(2)}
                      </span>
                      <span className="text-text-tertiary">/month per location</span>
                    </div>
                    {/* Final Total */}
                    <div className="pt-2 border-t border-[#504A4A]">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-text-primary">
                          ${selectedPlan.monthlyPrice.toFixed(2)}
                        </span>
                        <span className="text-text-tertiary">/month total</span>
                      </div>
                      <p className="text-xs text-text-tertiary mt-1">
                        {locationCount} location{locationCount !== 1 ? 's' : ''} × ${(foundersMonthlyPrice * (1 - selectedPlan.discount / 100)).toFixed(2)}/location
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-text-primary">
                      ${selectedPlan.monthlyPrice.toFixed(2)}
                    </span>
                    <span className="text-text-tertiary">/month</span>
                  </div>
                )}
                {selectedPlan.isGoldenTicket && (
                  <>
                    <div className="mt-3 p-4 bg-yellow-500/10 border-2 border-yellow-500/40 rounded-lg">
                      <div className="flex items-start gap-2 mb-2">
                        <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-yellow-300 mb-2">
                            Founders Circle: First 10 Seats Only
                          </p>
                          <div className="space-y-2 text-xs text-text-secondary">
                            <p className="font-semibold text-yellow-400/90">Exclusive First 10 Benefits:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                              <li>CEO will personally fly out to your gym</li>
                              <li>CEO personally handles your complete on-site onboarding</li>
                              <li>Live tech staff support during conversion to troubleshoot and debug any issues</li>
                              <li>Premium access to CEO's personal cell phone - call anytime you need help</li>
                              <li>Dedicated support during your entire conversion process</li>
                            </ul>
                            <p className="text-text-tertiary mt-2 italic">
                              All Equity Partners receive tailored onboarding support. On-site onboarding may expand to all members in the future.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 p-4 bg-gradient-to-r from-yellow-500/20 to-amber-500/15 border-2 border-yellow-400/40 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-yellow-300 mb-1">
                            Equity Partner Opportunity
                          </p>
                          <p className="text-xs text-text-secondary leading-relaxed">
                            As an Equity Partner, you're not just a customer—you're an investor in RollOps. 
                            This tier includes the opportunity for revenue share participation and a private equity stake 
                            in RollOps (terms to be discussed upon enrollment). You're building the future of gym management with us.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setSelectedPlanId(null)}
                className="text-text-tertiary hover:text-text-primary transition-colors text-2xl"
                aria-label="Clear selection"
              >
                ×
              </button>
            </div>

            {/* Core Features - Same for All */}
            <div className="mb-6 pb-6 border-b border-[#504A4A]">
              <h3 className="text-lg font-semibold text-text-primary mb-4">RollOps Core Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {coreFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bonus Features - Plan Specific */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                {selectedPlan.id === '3year' ? 'Golden Ticket Benefits' : 'What Makes This Plan Special'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getBonusFeatures(selectedPlan).map((bonus, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">{bonus}</span>
                  </div>
                ))}
              </div>
              
              {/* Full perks list for Golden Ticket */}
              {selectedPlan.id === '3year' && selectedPlan.perks && selectedPlan.perks.length > 5 && (
                <div className="mt-6 pt-6 border-t border-[#504A4A]">
                  <h4 className="text-md font-semibold text-text-primary mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-400" />
                    All Champions Club Perks
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedPlan.perks.slice(5).map((perk, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Star className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-text-tertiary">{perk}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <div className="mt-8 pt-6 border-t border-[#504A4A]">
              <Button
                onClick={handleProceedToCheckout}
                className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-text-primary font-semibold py-4 text-lg flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Get Started with {selectedPlan.name}</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
              {selectedPlan.discount > 0 ? (
                <p className="text-xs text-text-tertiary text-center mt-3">
                  {locationCount} location{locationCount !== 1 ? 's' : ''} × ${(foundersMonthlyPrice * (1 - selectedPlan.discount / 100)).toFixed(2)}/location = ${selectedPlan.monthlyPrice.toFixed(2)}/month
                </p>
              ) : (
                <p className="text-xs text-text-tertiary text-center mt-3">
                  {locationCount} location{locationCount !== 1 ? 's' : ''} × ${foundersMonthlyPrice}/location
                </p>
              )}
            </div>
          </div>
        )}

        {/* Core Features Overview - Always Visible */}
        {!selectedPlan && (
          <div className="bg-[#221F1F] rounded-xl border border-[#504A4A] p-8 mb-6">
            <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
              Everything You Need to Run Your Gym
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#9B1421]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-text-primary" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">Member Management</h3>
                <p className="text-sm text-text-secondary">
                  Complete member profiles, attendance tracking, and enrollment management
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#9B1421]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <DollarSign className="w-6 h-6 text-text-primary" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">Zero Processing Fees</h3>
                <p className="text-sm text-text-secondary">
                  Keep 100% of your subscription revenue - legally pass fees to customers
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#9B1421]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-6 h-6 text-text-primary" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">Class Scheduling</h3>
                <p className="text-sm text-text-secondary">
                  Create schedules, manage enrollments, and track attendance automatically
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Existing Subscription Display */}
        {subscription && (
          <div className="bg-[#363232] rounded-xl border border-[#504A4A] p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Current Subscription</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Plan</p>
                <p className="text-text-primary font-semibold capitalize">{subscription.plan_type}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Billing Cycle</p>
                <p className="text-text-primary font-semibold capitalize">{subscription.billing_cycle}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Price</p>
                <p className="text-text-primary font-semibold">
                  ${subscription.price.toFixed(2)} / {subscription.billing_cycle === 'monthly' ? 'month' : 'year'}
                </p>
              </div>
            </div>
            <p className="text-sm text-text-secondary mt-4">
              Need to make changes? Contact support for assistance with upgrades, downgrades, or cancellations.
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
